import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChatLauncherSpacer } from "@/components/layout/ChatLauncherSpacer";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import { NotionStyleLayout } from "@/components/building/NotionStyleLayout";
import { BuildingHeader } from "@/components/building/BuildingHeader";
import { DocumentsSection } from "@/components/building/DocumentsSection";
import { DashboardProjectsGrid } from "@/domains/documents/components/DashboardProjectsGrid";
import { BuildingDataSummary } from "@/components/building/BuildingDataSummary";
import { useBuildingAttributes } from "@/hooks/buildings/useBuildingAttributes";
import { useBuildingDocumentStats } from "@/hooks/buildings/useBuildingDocumentStats";
import { useBuildingViewMode } from "@/hooks/buildings/useBuildingViewMode";
import { usePermissions } from "@/hooks/usePermissions";
import type { Translations } from "@/lib/i18n/types";
import type { BuildingRow } from "@/stores/buildings/models";
import { debugLog } from "@/utils/debugLog";

/** The building as BuildingDetail maps it from the store — ids stringified. */
export interface BuildingViewData {
  building: {
    id: string;
    org_id: string;
    name: string;
    address: string;
    registry_code?: string | null;
  };
}

export interface BuildingViewProps {
  data: BuildingViewData | null;
  isLoading: boolean;
  error: Error | null;
  onBuildingChange: (updatedFields: Partial<BuildingRow>) => Promise<void>;
  onFieldsSave: (fields: Record<string, string>) => Promise<void>;
  /** Active building scope. When set, the dashboard stats/charts are scoped to
   * this project (`building_projects.id`). Undefined = whole building. */
  projectId?: string;
  t: Translations;
}

export function BuildingView({
  data,
  isLoading,
  error,
  onBuildingChange,
  onFieldsSave,
  projectId,
  t,
}: BuildingViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const perms = usePermissions();
  const buildingId = data?.building?.id;
  const orgId = data?.building?.org_id;
  // Clicking a project card jumps into that project's perspective, so the
  // sidebar must flip to Projects view first — Building view strips the
  // `?project` scope param the navigation carries.
  const { setViewMode } = useBuildingViewMode(buildingId ? String(buildingId) : "");
  const { data: attributes } = useBuildingAttributes(
    buildingId ? String(buildingId) : undefined,
    orgId
  );
  const docStats = useBuildingDocumentStats(buildingId ? String(buildingId) : undefined, {
    timeRange: "30d",
    projectId,
  });

  // Add debugging
  debugLog("BuildingView received data:", data);

  const handleFieldsSave = async (fields: Record<string, string>) => {
    await onFieldsSave(fields);
    // Invalidate the attributes query so the UI reflects saved values
    queryClient.invalidateQueries({
      queryKey: ["building-header-attributes", String(buildingId), orgId],
    });
  };

  // Loading UX is owned by BuildingLoadingOverlay in BuildingDetail.
  // Render nothing here so the overlay's fade-in doesn't reveal a second
  // spinner behind it.
  if (isLoading) {
    return null;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTitle>{t.common.error}</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : t.buildings.buildingNotFound}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!data || !data.building) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTitle>{t.buildings.buildingNotFound}</AlertTitle>
          <AlertDescription>{t.buildings.buildingNotFound}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const building = data.building;

  return (
    <NotionStyleLayout>
      {/* Each dashboard section is a small uppercase label row (with its
          controls) on the white page, above a white card holding
          the section's content (DashboardPanel) — vertical spacing between
          panels does the separating, no hairline dividers. The label rows and
          panels are rendered by the section components themselves. */}
      <div className="space-y-6">
        {/* Section 1: page title + inline-editable building metadata — stays
            directly on the white page, no panel. */}
        <section>
          <BuildingHeader
            building={building}
            attributes={attributes}
            onFieldsSave={handleFieldsSave}
            t={t}
          />
        </section>

        {/* Section 2: Projects — the project picker only makes sense at
            whole-building scope. When a project is selected in the header the
            dashboard is already narrowed to it, so the grid is hidden. The
            section label row (with its "See all" link) is rendered by the grid
            itself. */}
        {!projectId && (
          <section>
            <DashboardProjectsGrid
              buildingId={building.id}
              orgId={building.org_id}
              onProjectSelect={(project) => {
                // Entering a project's context lands on its dashboard. Flip
                // the sidebar to Projects view first; Building view strips the
                // `?project` scope param the navigation carries.
                setViewMode("projects");
                navigate(`/buildings/${building.id}/project-dashboard?project=${project.id}`);
              }}
            />
          </section>
        )}

        {/* Section 3: Building data — full width, below Projects. The message
            board lives on the Project Dashboard, not here. */}
        {perms.canViewBuildingDataSidebar(Number(building.id)) && (
          <section>
            <BuildingDataSummary buildingId={String(building.id)} orgId={building.org_id} />
          </section>
        )}

        {/* Section 4: Document analytics — hidden when no documents exist (in
            the active scope). Charts narrow to the scoped project when one is
            selected. */}
        {docStats.total > 0 && (
          <section>
            <DocumentsSection buildingId={building.id} projectId={projectId} t={t} />
          </section>
        )}
        <ChatLauncherSpacer />
      </div>
    </NotionStyleLayout>
  );
}
